// useFacilitiesCleaning — batched "who's cleaning / who to call" fetch.
//
// Given a set of facility_ids, returns a Map<facility_id, FacilityCleaningInfo>
// built from each facility's Active service_assignments joined to
// service_providers. Used by:
//   • My Accounts list  → all visible facility ids in one round-trip.
//   • Facility Detail    → the single facility on the Overview tab.
//
// Follows the TCS flat-fetch convention (no PostgREST embeds): pull Active
// assignments via an IN filter, then hydrate provider POC fields via a second
// IN-lookup on service_providers, then join client-side. Resilient to FK /
// schema-cache drift. See docs/database-changes.md.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import {
  buildCleaningInfo,
  type CleaningAssignment,
  type FacilityCleaningInfo,
} from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { catalogLabel } from '../../../shared/service-roles';

interface AssignmentRow {
  facility_id: string | null;
  provider_id: string | null;
  service_code: string | null;
  worker_name: string | null;
  // Embedded from service_catalog through the line's validated reference, so the
  // words a person reads come from the list rather than from the stored string.
  // ⚠️ The generated types describe a to-one embed as an ARRAY. Normalised on
  // read rather than asserted away, because asserting it away is how a shape
  // mismatch reaches a screen as `undefined`.
  service_catalog?: { display_name: string | null }[] | { display_name: string | null } | null;
}

interface ProviderRow {
  provider_id: string;
  partner_name: string | null;
  primary_poc_name: string | null;
  primary_poc_phone: string | null;
  primary_poc_email: string | null;
  secondary_poc_name: string | null;
  owner_name: string | null;
}

export interface UseFacilitiesCleaningResult {
  /** facility_id → collapsed two-line display info. */
  cleaning: Map<string, FacilityCleaningInfo>;
  loading: boolean;
  error: string | null;
}

export function useFacilitiesCleaning(
  facilityIds: string[],
): UseFacilitiesCleaningResult {
  const [cleaning, setCleaning] = useState<Map<string, FacilityCleaningInfo>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable key so the effect only re-runs when the id SET changes, not on
  // every render that hands us a fresh array instance.
  const idKey = useMemo(
    () => Array.from(new Set(facilityIds)).sort().join(','),
    [facilityIds],
  );

  useEffect(() => {
    const ids = idKey ? idKey.split(',') : [];
    if (ids.length === 0) {
      setCleaning(new Map());
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: aData, error: aErr } = await supabase
          .from('service_assignments')
          .select('facility_id, provider_id, service_code, worker_name, service_catalog(display_name)')
          .in('facility_id', ids)
          .eq('assignment_status', 'Active');
        if (aErr) throw aErr;
        const assignments = (aData ?? []) as AssignmentRow[];

        const providerIds = Array.from(
          new Set(
            assignments
              .map((a) => a.provider_id)
              .filter((v): v is string => typeof v === 'string' && Boolean(v)),
          ),
        );
        const provMap = new Map<string, ProviderRow>();
        if (providerIds.length > 0) {
          const { data: pData, error: pErr } = await supabase
            .from('service_providers')
            .select(
              'provider_id, partner_name, primary_poc_name, primary_poc_phone, primary_poc_email, secondary_poc_name, owner_name',
            )
            .in('provider_id', providerIds);
          if (pErr) throw pErr;
          for (const p of (pData ?? []) as ProviderRow[]) {
            provMap.set(p.provider_id, p);
          }
        }

        // Group assignments by facility, hydrating provider POC fields.
        const byFacility = new Map<string, CleaningAssignment[]>();
        for (const a of assignments) {
          if (!a.facility_id) continue;
          const prov = a.provider_id ? provMap.get(a.provider_id) : undefined;
          const row: CleaningAssignment = {
            service_code: a.service_code,
            service_label: catalogLabel(a.service_catalog),
            worker_name: a.worker_name,
            partner_name: prov?.partner_name ?? null,
            primary_poc_name: prov?.primary_poc_name ?? null,
            primary_poc_phone: prov?.primary_poc_phone ?? null,
            primary_poc_email: prov?.primary_poc_email ?? null,
            secondary_poc_name: prov?.secondary_poc_name ?? null,
            owner_name: prov?.owner_name ?? null,
          };
          const arr = byFacility.get(a.facility_id);
          if (arr) arr.push(row);
          else byFacility.set(a.facility_id, [row]);
        }

        const next = new Map<string, FacilityCleaningInfo>();
        for (const [fid, rows] of byFacility) {
          next.set(fid, buildCleaningInfo(rows));
        }
        if (!cancelled) setCleaning(next);
      } catch (e) {
        if (!cancelled) {
          console.error('[use-facility-cleaning] load failed', e);
          setError(errorMessage(e, 'Failed to load cleaning info'));
          setCleaning(new Map());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [idKey]);

  return { cleaning, loading, error };
}
